import React, { useState } from "react";
import { Modal, Form, Spinner, Row, Col, Badge } from "react-bootstrap";
import userAvatar from "../assets/img/img1.jpg";
import Footer from "../layouts/Footer";
import { useAuth } from "../hooks/useAuth";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
});

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('❌ Erreur API:', error);
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default function MonCompteOrganisation() {
  const [showActivateMfaModal, setShowActivateMfaModal] = useState(false);
  const [showDeactivateMfaModal, setShowDeactivateMfaModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { user, logout, updateUser } = useAuth();

  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // ============================================
  // CHANGEMENT DE MOT DE PASSE
  // ============================================
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordError('');

    if (!passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordError('Tous les champs sont obligatoires');
      setPasswordLoading(false);
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('Les nouveaux mots de passe ne correspondent pas');
      setPasswordLoading(false);
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setPasswordError('Le mot de passe doit contenir au moins 8 caractères');
      setPasswordLoading(false);
      return;
    }

    try {
      const response = await apiClient.post('/connected-update-password', {
        new_password: passwordData.newPassword
      });

      const data = response.data;

      if (data.code === 200 || response.status === 200) {
        setPasswordSuccess(data.message || 'Mot de passe changé avec succès !');
        setTimeout(() => {
          logout();
          window.location.href = '/';
        }, 4000);
      } else {
        setPasswordError(data.message || 'Erreur lors du changement de mot de passe');
      }
    } catch (error) {
      console.error('❌ Erreur changement mot de passe:', error);
      const errorMsg = error.response?.data?.message || 'Erreur de connexion. Veuillez réessayer.';
      setPasswordError(errorMsg);
    } finally {
      setPasswordLoading(false);
    }
  };

  // ============================================
  // ACTIVATION / DÉSACTIVATION 2FA
  // ============================================
  const handleActivateMfa = async (e) => {
    e.preventDefault();
    setMfaLoading(true);
    setMfaError("");

    try {
      const response = await apiClient.put('/set-client-mfa', { has_mfa: "YES" });
      const data = response.data;

      if (data.code === 200) {
        updateUser({ has_mfa: "YES" });
        setShowActivateMfaModal(false);
        setMfaError("");
      } else {
        setMfaError(data.message || "Erreur lors de l'activation de la 2FA");
      }
    } catch (error) {
      console.error('❌ Erreur activation 2FA:', error);
      const errorMsg = error.response?.data?.message || "Erreur de connexion. Veuillez réessayer.";
      setMfaError(errorMsg);
    } finally {
      setMfaLoading(false);
    }
  };

  const handleDeactivateMfa = async (e) => {
    e.preventDefault();
    setMfaLoading(true);
    setMfaError("");

    try {
      const response = await apiClient.put('/set-client-mfa', { has_mfa: "NO" });
      const data = response.data;

      if (data.code === 200) {
        updateUser({ has_mfa: "NO" });
        setShowDeactivateMfaModal(false);
        setMfaError("");
      } else {
        setMfaError(data.message || "Erreur lors de la désactivation de la 2FA");
      }
    } catch (error) {
      console.error('❌ Erreur désactivation 2FA:', error);
      const errorMsg = error.response?.data?.message || "Erreur de connexion. Veuillez réessayer.";
      setMfaError(errorMsg);
    } finally {
      setMfaLoading(false);
    }
  };

  // ============================================
  // LOADING
  // ============================================
  if (!user) {
    return (
      <div className="pf-page main main-app d-flex justify-content-center align-items-center">
        <div className="pf-loading-wrapper">
          <div className="pf-loading-spinner">
            <div className="pf-loading-icon">
              <i className="bi bi-person-circle"></i>
            </div>
          </div>
          <h5 className="pf-loading-title">Chargement du profil</h5>
          <p className="pf-loading-text">Veuillez patienter...</p>
          <div className="pf-loading-dots">
            <span></span><span></span><span></span>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // HELPERS
  // ============================================
  const getInitials = () => {
    const first = user?.firstname?.charAt(0)?.toUpperCase() || '';
    const last = user?.lastname?.charAt(0)?.toUpperCase() || '';
    return `${first}${last}` || 'U';
  };

  const formatMemberSince = () => {
    if (!user.reg_date) return 'Date non disponible';
    return new Date(user.reg_date).toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).replace(/ à /, ' ');
  };

  const isMfaActive = user?.has_mfa === "YES";

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="pf-page main main-app p-3 p-lg-4">
      {/* ========== HEADER PREMIUM ========== */}
      <div className="pf-header mb-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="pf-header-icon">
              <i className="bi bi-person-badge-fill"></i>
            </div>
            <div>
              <h3 className="pf-header-title mb-1">Mon profil</h3>
              <p className="pf-header-subtitle mb-0">
                <i className="bi bi-shield-lock"></i>
                Gérez vos informations personnelles et préférences de sécurité
              </p>
            </div>
          </div>
          <div className="pf-header-badge">
            <span className={`pf-status-dot ${isMfaActive ? 'pf-status-dot-ok' : 'pf-status-dot-pending'}`}></span>
            <span>{isMfaActive ? 'Sécurité maximale' : 'Sécurité standard'}</span>
          </div>
        </div>
      </div>

      {/* ========== INFO BANNER ========== */}
      <div className="pf-info-banner mb-4">
        <div className="pf-info-banner-icon">
          <i className="bi bi-info-circle-fill"></i>
        </div>
        <div className="pf-info-banner-content">
          <div className="pf-info-banner-title">Complétez votre profil</div>
          <div className="pf-info-banner-text">
            Remplissez les détails de votre organisation afin d'accéder à plus de services.
          </div>
        </div>
        <button className="pf-info-banner-btn">
          <i className="bi bi-arrow-right"></i>
          <span>Compléter</span>
        </button>
      </div>

      {/* ========== HERO PROFIL ========== */}
      <div className="pf-hero-card mb-4">
        <div className="pf-hero-banner"></div>
        <div className="pf-hero-body">
          <div className="pf-hero-avatar-wrapper">
            <img src={userAvatar} alt="Avatar" className="pf-hero-avatar" />
            <div className="pf-hero-avatar-status">
              <i className="bi bi-check-circle-fill"></i>
            </div>
          </div>

          <div className="pf-hero-info">
            <h4 className="pf-hero-name">
              {user.firstname && user.lastname 
                ? `${user.firstname} ${user.lastname}` 
                : getInitials()}
            </h4>
            <div className="pf-hero-email">
              <i className="bi bi-envelope-fill"></i>
              {user.business_email || "Email non disponible"}
            </div>
            <div className="pf-hero-badges">
              <span className="pf-hero-badge pf-hero-badge-success">
                <i className="bi bi-patch-check-fill"></i>
                Vérifié
              </span>
              <span className="pf-hero-badge pf-hero-badge-blue">
                <i className="bi bi-person-badge"></i>
                {user.acc_type === 'business' ? 'Entreprise' : 'Utilisateur'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ========== INFO CARDS ========== */}
      <Row className="g-3 mb-4">
        <Col lg={4} md={6}>
          <div className="pf-info-card">
            <div className="pf-info-card-icon pf-info-card-icon-blue">
              <i className="bi bi-hash"></i>
            </div>
            <div className="pf-info-card-content">
              <div className="pf-info-card-label">ID utilisateur</div>
              <div className="pf-info-card-value pf-code">
                {user.id || "4676b696-2354-42ea-95b1-ca91fb897c0d"}
              </div>
            </div>
          </div>
        </Col>

        <Col lg={4} md={6}>
          <div className="pf-info-card">
            <div className="pf-info-card-icon pf-info-card-icon-green">
              <i className="bi bi-calendar-check"></i>
            </div>
            <div className="pf-info-card-content">
              <div className="pf-info-card-label">Membre depuis</div>
              <div className="pf-info-card-value">
                {formatMemberSince()}
              </div>
            </div>
          </div>
        </Col>

        <Col lg={4} md={12}>
          <div className="pf-info-card">
            <div className="pf-info-card-icon pf-info-card-icon-violet">
              <i className="bi bi-envelope-at"></i>
            </div>
            <div className="pf-info-card-content">
              <div className="pf-info-card-label">Adresse email</div>
              <div className="pf-info-card-value pf-info-card-email">
                <span>{user.business_email || "Email non disponible"}</span>
                <span className="pf-primary-badge">Principal</span>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* ========== SECTION SÉCURITÉ ========== */}
      <div className="pf-section-header mb-3">
        <div className="pf-section-header-icon">
          <i className="bi bi-shield-fill-check"></i>
        </div>
        <div>
          <h5 className="pf-section-title">Sécurité du compte</h5>
          <p className="pf-section-subtitle">Protégez votre compte avec ces options de sécurité</p>
        </div>
      </div>

      <Row className="g-3 mb-4">
        {/* Mot de passe */}
        <Col lg={6}>
          <div className="pf-security-card">
            <div className="pf-security-card-header">
              <div className="pf-security-card-icon pf-security-card-icon-amber">
                <i className="bi bi-shield-lock-fill"></i>
              </div>
              <div className="pf-security-card-title-wrapper">
                <h6 className="pf-security-card-title">Mot de passe</h6>
                <span className="pf-security-card-subtitle">Sécurité du compte</span>
              </div>
              <span className="pf-security-badge pf-security-badge-success">
                <i className="bi bi-check-circle-fill"></i>
                Sécurisé
              </span>
            </div>

            <div className="pf-security-card-body">
              <p className="pf-security-card-text">
                Votre mot de passe doit répondre aux exigences minimales de sécurité pour protéger votre compte.
              </p>

              <div className="pf-security-info-box">
                <div className="pf-security-info-row">
                  <span className="pf-security-info-label">
                    <i className="bi bi-clock-history"></i>
                    Dernière modification
                  </span>
                  <span className="pf-security-info-value pf-security-info-value-success">
                    Il y a 15 jours
                  </span>
                </div>
              </div>
            </div>

            <div className="pf-security-card-footer">
              <button 
                className="pf-btn-primary pf-btn-full"
                onClick={() => setShowChangePasswordModal(true)}
              >
                <i className="bi bi-key-fill"></i>
                <span>Changer le mot de passe</span>
              </button>
            </div>
          </div>
        </Col>

        {/* 2FA */}
        <Col lg={6}>
          <div className={`pf-security-card ${isMfaActive ? 'pf-security-card-active' : ''}`}>
            <div className="pf-security-card-header">
              <div className={`pf-security-card-icon ${isMfaActive ? 'pf-security-card-icon-green' : 'pf-security-card-icon-amber'}`}>
                <i className={`bi ${isMfaActive ? 'bi-phone-fill' : 'bi-phone'}`}></i>
              </div>
              <div className="pf-security-card-title-wrapper">
                <h6 className="pf-security-card-title">
                  {isMfaActive ? '2FA Activé' : '2FA Désactivé'}
                </h6>
                <span className="pf-security-card-subtitle">Double authentification</span>
              </div>
              <span className={`pf-security-badge ${isMfaActive ? 'pf-security-badge-success' : 'pf-security-badge-warning'}`}>
                <i className={`bi ${isMfaActive ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`}></i>
                {isMfaActive ? 'Actif' : 'Inactif'}
              </span>
            </div>

            <div className="pf-security-card-body">
              <p className="pf-security-card-text">
                {isMfaActive 
                  ? "L'authentification à deux facteurs ajoute une couche de sécurité supplémentaire à votre compte."
                  : "Activez l'authentification à deux facteurs pour sécuriser davantage votre compte."
                }
              </p>

              <div className="pf-security-info-box">
                <div className="pf-security-info-row">
                  <span className="pf-security-info-label">
                    <i className="bi bi-shield-check"></i>
                    Statut
                  </span>
                  <span className={`pf-security-info-value ${isMfaActive ? 'pf-security-info-value-success' : 'pf-security-info-value-warning'}`}>
                    {isMfaActive ? 'Protégé par 2FA' : 'Non protégé'}
                  </span>
                </div>
              </div>
            </div>

            <div className="pf-security-card-footer">
              {isMfaActive ? (
                <button 
                  className="pf-btn-danger pf-btn-full"
                  onClick={() => setShowDeactivateMfaModal(true)}
                >
                  <i className="bi bi-shield-slash-fill"></i>
                  <span>Désactiver la 2FA</span>
                </button>
              ) : (
                <button 
                  className="pf-btn-success pf-btn-full"
                  onClick={() => setShowActivateMfaModal(true)}
                >
                  <i className="bi bi-shield-check"></i>
                  <span>Activer la 2FA</span>
                </button>
              )}
            </div>
          </div>
        </Col>
      </Row>

      {/* ========== PRÉFÉRENCES ========== */}
      <div className="pf-section-header mb-3">
        <div className="pf-section-header-icon pf-section-header-icon-violet">
          <i className="bi bi-sliders"></i>
        </div>
        <div>
          <h5 className="pf-section-title">Préférences</h5>
          <p className="pf-section-subtitle">Personnalisez votre expérience</p>
        </div>
      </div>

      <Row className="g-3 mb-4">
        <Col md={6}>
          <div className="pf-preference-card">
            <div className="pf-preference-icon-wrapper pf-preference-icon-wrapper-blue">
              <i className="bi bi-bell-fill"></i>
            </div>
            <div className="pf-preference-content">
              <div className="pf-preference-title">Notifications</div>
              <div className="pf-preference-desc">Email et notifications push</div>
            </div>
            <label className="pf-switch">
              <input type="checkbox" defaultChecked />
              <span className="pf-switch-slider"></span>
            </label>
          </div>
        </Col>

        <Col md={6}>
          <div className="pf-preference-card">
            <div className="pf-preference-icon-wrapper pf-preference-icon-wrapper-violet">
              <i className="bi bi-globe2"></i>
            </div>
            <div className="pf-preference-content">
              <div className="pf-preference-title">Langue</div>
              <div className="pf-preference-desc">Français (France)</div>
            </div>
            <span className="pf-lang-badge">FR</span>
          </div>
        </Col>
      </Row>

      {/* ========== MODAL CHANGEMENT MOT DE PASSE ========== */}
      <Modal 
        show={showChangePasswordModal} 
        onHide={() => {
          setShowChangePasswordModal(false);
          setPasswordError('');
          setPasswordSuccess('');
          setPasswordData({ newPassword: '', confirmPassword: '' });
        }}
        centered
        size="lg"
        className="pf-modal"
      >
        <div className="pf-modal-header pf-modal-header-amber">
          <div className="d-flex align-items-center gap-3">
            <div className="pf-modal-icon">
              <i className="bi bi-key-fill"></i>
            </div>
            <div>
              <h5 className="pf-modal-title mb-0">Changer le mot de passe</h5>
              <small className="pf-modal-subtitle">Sécurisez votre compte</small>
            </div>
          </div>
          <button 
            className="pf-modal-close" 
            onClick={() => {
              setShowChangePasswordModal(false);
              setPasswordError('');
              setPasswordSuccess('');
              setPasswordData({ newPassword: '', confirmPassword: '' });
            }}
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <Modal.Body className="pf-modal-body">
          {passwordError && (
            <div className="pf-alert pf-alert-danger mb-3">
              <i className="bi bi-exclamation-triangle-fill"></i>
              <div>{passwordError}</div>
            </div>
          )}

          {passwordSuccess && (
            <div className="pf-alert pf-alert-success mb-3">
              <i className="bi bi-check-circle-fill"></i>
              <div>{passwordSuccess}</div>
            </div>
          )}

          <Form onSubmit={handlePasswordChange}>
            <Row className="g-3">
              <Col md={6}>
                <div className="pf-form-group">
                  <label className="pf-form-label">
                    <i className="bi bi-lock-fill pf-form-icon"></i>
                    Nouveau mot de passe <span className="pf-required">*</span>
                  </label>
                  <div className="pf-input-wrapper">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      className="pf-input"
                      placeholder="Nouveau mot de passe"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                      required
                    />
                    <button
                      type="button"
                      className="pf-input-toggle"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      <i className={`bi ${showNewPassword ? "bi-eye-slash-fill" : "bi-eye-fill"}`}></i>
                    </button>
                  </div>
                  <div className="pf-form-hint">
                    <i className="bi bi-info-circle"></i>
                    Minimum 8 caractères
                  </div>
                </div>
              </Col>

              <Col md={6}>
                <div className="pf-form-group">
                  <label className="pf-form-label">
                    <i className="bi bi-lock-fill pf-form-icon"></i>
                    Confirmer <span className="pf-required">*</span>
                  </label>
                  <div className="pf-input-wrapper">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      className="pf-input"
                      placeholder="Confirmer le mot de passe"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                      required
                    />
                    <button
                      type="button"
                      className="pf-input-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      <i className={`bi ${showConfirmPassword ? "bi-eye-slash-fill" : "bi-eye-fill"}`}></i>
                    </button>
                  </div>
                </div>
              </Col>
            </Row>

            <div className="pf-security-requirements mt-4">
              <div className="pf-security-requirements-header">
                <i className="bi bi-shield-check"></i>
                <span>Exigences de sécurité</span>
              </div>
              <div className="pf-security-requirements-grid">
                {[
                  'Minimum 8 caractères',
                  'Lettres majuscules et minuscules',
                  'Au moins un chiffre',
                  'Au moins un caractère spécial'
                ].map((req, i) => (
                  <div key={i} className="pf-security-requirement">
                    <i className="bi bi-check-circle-fill"></i>
                    <span>{req}</span>
                  </div>
                ))}
              </div>
            </div>
          </Form>
        </Modal.Body>

        <div className="pf-modal-footer">
          <button 
            className="pf-btn-cancel"
            onClick={() => {
              setShowChangePasswordModal(false);
              setPasswordError('');
              setPasswordSuccess('');
              setPasswordData({ newPassword: '', confirmPassword: '' });
            }}
            disabled={passwordLoading}
          >
            Annuler
          </button>
          <button 
            className="pf-btn-primary"
            onClick={handlePasswordChange}
            disabled={!passwordData.newPassword || !passwordData.confirmPassword || passwordLoading}
          >
            {passwordLoading ? (
              <><Spinner animation="border" size="sm" /> Modification...</>
            ) : (
              <><i className="bi bi-check-lg"></i> Confirmer</>
            )}
          </button>
        </div>
      </Modal>

      {/* ========== MODAL ACTIVATION 2FA ========== */}
      <Modal 
        show={showActivateMfaModal} 
        onHide={() => {
          setShowActivateMfaModal(false);
          setMfaError("");
        }} 
        centered 
        className="pf-modal"
      >
        <div className="pf-modal-header pf-modal-header-success">
          <div className="d-flex align-items-center gap-3">
            <div className="pf-modal-icon">
              <i className="bi bi-shield-check"></i>
            </div>
            <div>
              <h5 className="pf-modal-title mb-0">Activer la 2FA</h5>
              <small className="pf-modal-subtitle">Renforcez votre sécurité</small>
            </div>
          </div>
          <button 
            className="pf-modal-close" 
            onClick={() => {
              setShowActivateMfaModal(false);
              setMfaError("");
            }}
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <Modal.Body className="pf-modal-body pf-modal-body-center">
          <div className="pf-confirm-icon pf-confirm-icon-success">
            <i className="bi bi-phone-fill"></i>
          </div>
          <h4 className="pf-confirm-title">Activer l'authentification à deux facteurs</h4>
          <p className="pf-confirm-text">
            L'authentification à deux facteurs ajoute une couche de sécurité supplémentaire à votre compte.
            Vous recevrez un code de vérification à chaque connexion.
          </p>

          {mfaError && (
            <div className="pf-alert pf-alert-danger mb-3">
              <i className="bi bi-exclamation-triangle-fill"></i>
              <div>{mfaError}</div>
            </div>
          )}
        </Modal.Body>

        <div className="pf-modal-footer">
          <button
            className="pf-btn-cancel pf-btn-flex"
            onClick={() => {
              setShowActivateMfaModal(false);
              setMfaError("");
            }}
            disabled={mfaLoading}
          >
            Annuler
          </button>
          <button
            className="pf-btn-success pf-btn-flex"
            onClick={handleActivateMfa}
            disabled={mfaLoading}
          >
            {mfaLoading ? (
              <><Spinner animation="border" size="sm" /> Activation...</>
            ) : (
              <><i className="bi bi-check-circle-fill"></i> Activer la 2FA</>
            )}
          </button>
        </div>
      </Modal>

      {/* ========== MODAL DÉSACTIVATION 2FA ========== */}
      <Modal 
        show={showDeactivateMfaModal} 
        onHide={() => {
          setShowDeactivateMfaModal(false);
          setMfaError("");
        }} 
        centered 
        className="pf-modal"
      >
        <div className="pf-modal-header pf-modal-header-danger">
          <div className="d-flex align-items-center gap-3">
            <div className="pf-modal-icon">
              <i className="bi bi-shield-slash-fill"></i>
            </div>
            <div>
              <h5 className="pf-modal-title mb-0">Désactiver la 2FA</h5>
              <small className="pf-modal-subtitle">Attention requise</small>
            </div>
          </div>
          <button 
            className="pf-modal-close" 
            onClick={() => {
              setShowDeactivateMfaModal(false);
              setMfaError("");
            }}
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <Modal.Body className="pf-modal-body pf-modal-body-center">
          <div className="pf-confirm-icon pf-confirm-icon-danger">
            <i className="bi bi-exclamation-triangle-fill"></i>
          </div>
          <h4 className="pf-confirm-title pf-confirm-title-danger">Désactiver la 2FA ?</h4>
          <p className="pf-confirm-text">
            La désactivation de la 2FA réduira considérablement la sécurité de votre compte.
            Êtes-vous sûr de vouloir continuer ?
          </p>

          <div className="pf-confirm-warning">
            <i className="bi bi-exclamation-triangle-fill"></i>
            <div>
              <strong>Attention</strong>
              <p className="mb-0 small">Votre compte sera moins protégé contre les accès non autorisés</p>
            </div>
          </div>

          {mfaError && (
            <div className="pf-alert pf-alert-danger mb-3">
              <i className="bi bi-exclamation-triangle-fill"></i>
              <div>{mfaError}</div>
            </div>
          )}
        </Modal.Body>

        <div className="pf-modal-footer">
          <button
            className="pf-btn-cancel pf-btn-flex"
            onClick={() => {
              setShowDeactivateMfaModal(false);
              setMfaError("");
            }}
            disabled={mfaLoading}
          >
            Annuler
          </button>
          <button
            className="pf-btn-danger pf-btn-flex"
            onClick={handleDeactivateMfa}
            disabled={mfaLoading}
          >
            {mfaLoading ? (
              <><Spinner animation="border" size="sm" /> Désactivation...</>
            ) : (
              <><i className="bi bi-x-circle-fill"></i> Désactiver la 2FA</>
            )}
          </button>
        </div>
      </Modal>

      <Footer />

      {/* ========== STYLES PREMIUM ========== */}
      <style>{`
        /* ========== GLOBAL ========== */
        .pf-page { background: #f8fafc; min-height: 100vh; }
        .pf-page * { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }

        /* ========== LOADING ========== */
        .pf-loading-wrapper { text-align: center; }
        .pf-loading-spinner {
          width: 100px; height: 100px; border-radius: 50%;
          background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 24px;
          animation: pf-pulse 2s ease-in-out infinite;
        }
        .pf-loading-icon { font-size: 42px; color: #3b82f6; animation: pf-bounce 1.5s ease-in-out infinite; }
        .pf-loading-title { color: #1e40af; font-weight: 700; margin-bottom: 8px; }
        .pf-loading-text { color: #64748b; font-size: 0.875rem; margin-bottom: 20px; }
        .pf-loading-dots { display: flex; justify-content: center; gap: 6px; }
        .pf-loading-dots span {
          width: 10px; height: 10px; border-radius: 50%; background: #3b82f6;
          animation: pf-dot 1.4s ease-in-out infinite both;
        }
        .pf-loading-dots span:nth-child(2) { animation-delay: 0.2s; }
        .pf-loading-dots span:nth-child(3) { animation-delay: 0.4s; }

        @keyframes pf-pulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4); }
          50% { transform: scale(1.05); box-shadow: 0 0 0 20px rgba(59, 130, 246, 0); }
        }
        @keyframes pf-bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes pf-dot {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }

        /* ========== HEADER ========== */
        .pf-header {
          background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
          border-radius: 16px; padding: 24px 28px;
          border: 1px solid #e2e8f0; box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .pf-header-icon {
          width: 56px; height: 56px; border-radius: 14px;
          background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
          color: white; display: flex; align-items: center; justify-content: center;
          font-size: 26px; box-shadow: 0 8px 20px -6px rgba(139, 92, 246, 0.5);
        }
        .pf-header-title {
          font-weight: 700; color: #0f172a; font-size: 1.5rem; letter-spacing: -0.02em;
        }
        .pf-header-subtitle {
          color: #64748b; font-size: 0.875rem;
          display: flex; align-items: center; gap: 6px;
        }
        .pf-header-subtitle i { color: #94a3b8; }
        .pf-header-badge {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 8px 16px; background: white;
          border-radius: 20px; font-size: 0.8rem; font-weight: 700;
          color: #334155; border: 1px solid #e2e8f0;
        }
        .pf-status-dot {
          width: 8px; height: 8px; border-radius: 50%;
        }
        .pf-status-dot-ok { background: #10b981; animation: pf-pulse 2s infinite; }
        .pf-status-dot-pending { background: #f59e0b; animation: pf-pulse 2s infinite; }

        /* ========== INFO BANNER ========== */
        .pf-info-banner {
          display: flex; align-items: center; gap: 16px;
          padding: 16px 20px;
          background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
          border-radius: 14px; border: 1px solid #bfdbfe;
          border-left: 4px solid #3b82f6;
          flex-wrap: wrap;
        }
        .pf-info-banner-icon {
          width: 44px; height: 44px; border-radius: 12px;
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white; display: flex; align-items: center; justify-content: center;
          font-size: 20px; flex-shrink: 0;
          box-shadow: 0 6px 16px -4px rgba(59, 130, 246, 0.4);
        }
        .pf-info-banner-content { flex: 1; min-width: 200px; }
        .pf-info-banner-title {
          font-weight: 700; color: #1e40af; font-size: 0.9rem; margin-bottom: 2px;
        }
        .pf-info-banner-text {
          color: #1e40af; font-size: 0.8rem; opacity: 0.9;
        }
        .pf-info-banner-btn {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 10px 18px; border-radius: 10px; border: none;
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white; font-weight: 600; font-size: 0.85rem;
          cursor: pointer; transition: all 0.2s;
        }
        .pf-info-banner-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 16px -4px rgba(59, 130, 246, 0.5);
        }

        /* ========== HERO PROFIL ========== */
        .pf-hero-card {
          background: white; border-radius: 20px;
          border: 1px solid #e2e8f0; overflow: hidden;
          box-shadow: 0 4px 12px -4px rgba(0,0,0,0.05);
        }
        .pf-hero-banner {
          height: 100px;
          background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 50%, #3b82f6 100%);
          position: relative;
        }
        .pf-hero-banner::after {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at 20% 100%, rgba(255,255,255,0.2) 0%, transparent 50%),
                      radial-gradient(circle at 80% 0%, rgba(255,255,255,0.15) 0%, transparent 50%);
        }
        .pf-hero-body {
          padding: 0 28px 28px;
          display: flex; gap: 24px;
          flex-wrap: wrap;
          margin-top: -50px;
        }
        .pf-hero-avatar-wrapper {
          position: relative; flex-shrink: 0;
        }
        .pf-hero-avatar {
          width: 130px; height: 130px;
          border-radius: 50%;
          object-fit: cover;
          border: 5px solid white;
          box-shadow: 0 10px 30px -10px rgba(0,0,0,0.2);
          background: white;
        }
        .pf-hero-avatar-status {
          position: absolute; bottom: 8px; right: 8px;
          width: 32px; height: 32px; border-radius: 50%;
          background: #10b981; color: white;
          display: flex; align-items: center; justify-content: center;
          font-size: 16px; border: 4px solid white;
          box-shadow: 0 4px 12px -2px rgba(16, 185, 129, 0.5);
        }
        .pf-hero-info {
          flex: 1; min-width: 200px;
          padding-top: 60px;
        }
        .pf-hero-name {
          font-weight: 700; color: #0f172a;
          font-size: 1.5rem; margin-bottom: 6px;
          letter-spacing: -0.02em;
        }
        .pf-hero-email {
          display: inline-flex; align-items: center; gap: 6px;
          color: #64748b; font-size: 0.875rem; margin-bottom: 12px;
        }
        .pf-hero-email i { color: #94a3b8; }
        .pf-hero-badges { display: flex; gap: 8px; flex-wrap: wrap; }
        .pf-hero-badge {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 6px 14px; border-radius: 20px;
          font-size: 0.75rem; font-weight: 700;
        }
        .pf-hero-badge-success { background: #d1fae5; color: #059669; }
        .pf-hero-badge-blue { background: #dbeafe; color: #1e40af; }

        /* ========== INFO CARDS ========== */
        .pf-info-card {
          display: flex; align-items: center; gap: 14px;
          padding: 18px 20px; background: white;
          border-radius: 14px; border: 1px solid #e2e8f0;
          transition: all 0.25s; height: 100%;
        }
        .pf-info-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px -6px rgba(0,0,0,0.08);
        }
        .pf-info-card-icon {
          width: 46px; height: 46px; border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          font-size: 20px; flex-shrink: 0;
        }
        .pf-info-card-icon-blue { background: #dbeafe; color: #3b82f6; }
        .pf-info-card-icon-green { background: #d1fae5; color: #10b981; }
        .pf-info-card-icon-violet { background: #ede9fe; color: #8b5cf6; }
        .pf-info-card-content { flex-grow: 1; min-width: 0; }
        .pf-info-card-label {
          font-size: 0.68rem; color: #94a3b8; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px;
        }
        .pf-info-card-value {
          font-weight: 700; color: #0f172a; font-size: 0.85rem;
          word-break: break-word;
        }
        .pf-info-card-email {
          display: flex; align-items: center; justify-content: space-between;
          gap: 8px;
        }
        .pf-primary-badge {
          display: inline-flex; align-items: center;
          padding: 2px 8px; border-radius: 5px;
          background: #dbeafe; color: #1e40af;
          font-size: 0.65rem; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.03em;
          flex-shrink: 0;
        }
        .pf-code {
          font-family: 'SF Mono', Menlo, Monaco, monospace;
          background: #f1f5f9; color: #1e40af;
          padding: 3px 8px; border-radius: 5px;
          font-size: 0.72rem; display: inline-block;
          max-width: 100%; overflow: hidden; text-overflow: ellipsis;
        }

        /* ========== SECTION HEADER ========== */
        .pf-section-header {
          display: flex; align-items: center; gap: 14px;
        }
        .pf-section-header-icon {
          width: 44px; height: 44px; border-radius: 12px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white; display: flex; align-items: center; justify-content: center;
          font-size: 20px; flex-shrink: 0;
          box-shadow: 0 6px 16px -4px rgba(16, 185, 129, 0.4);
        }
        .pf-section-header-icon-violet {
          background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
          box-shadow: 0 6px 16px -4px rgba(139, 92, 246, 0.4);
        }
        .pf-section-title {
          font-weight: 700; color: #0f172a;
          margin: 0 0 2px; font-size: 1.05rem;
        }
        .pf-section-subtitle {
          color: #64748b; font-size: 0.8rem; margin: 0;
        }

        /* ========== SECURITY CARDS ========== */
        .pf-security-card {
          background: white; border-radius: 16px;
          border: 1px solid #e2e8f0; overflow: hidden;
          transition: all 0.25s; height: 100%;
          display: flex; flex-direction: column;
        }
        .pf-security-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 30px -10px rgba(0,0,0,0.1);
        }
        .pf-security-card-active {
          border-color: #a7f3d0;
        }
        .pf-security-card-header {
          display: flex; align-items: center; gap: 14px;
          padding: 20px 22px;
          border-bottom: 1px solid #f1f5f9;
          background: #f8fafc;
        }
        .pf-security-card-icon {
          width: 46px; height: 46px; border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          font-size: 20px; flex-shrink: 0;
        }
        .pf-security-card-icon-amber {
          background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
          color: #d97706;
        }
        .pf-security-card-icon-green {
          background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
          color: #059669;
        }
        .pf-security-card-title-wrapper { flex-grow: 1; min-width: 0; }
        .pf-security-card-title {
          font-weight: 700; color: #0f172a;
          font-size: 0.95rem; margin: 0 0 2px;
        }
        .pf-security-card-subtitle {
          font-size: 0.72rem; color: #94a3b8;
        }
        .pf-security-badge {
          display: inline-flex; align-items: center; gap: 4px;
          padding: 5px 10px; border-radius: 6px;
          font-size: 0.68rem; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.03em;
          flex-shrink: 0;
        }
        .pf-security-badge-success { background: #d1fae5; color: #059669; }
        .pf-security-badge-warning { background: #fef3c7; color: #92400e; }
        .pf-security-card-body { padding: 20px 22px; flex-grow: 1; }
        .pf-security-card-text {
          color: #64748b; font-size: 0.85rem;
          line-height: 1.5; margin-bottom: 16px;
        }
        .pf-security-info-box {
          background: #f8fafc; border-radius: 10px;
          padding: 12px 16px; border: 1px solid #f1f5f9;
        }
        .pf-security-info-row {
          display: flex; justify-content: space-between;
          align-items: center;
        }
        .pf-security-info-label {
          display: inline-flex; align-items: center; gap: 6px;
          font-size: 0.78rem; color: #64748b; font-weight: 600;
        }
        .pf-security-info-label i { color: #94a3b8; }
        .pf-security-info-value {
          font-size: 0.78rem; font-weight: 700;
        }
        .pf-security-info-value-success { color: #059669; }
        .pf-security-info-value-warning { color: #d97706; }
        .pf-security-card-footer {
          padding: 16px 22px 20px;
          border-top: 1px solid #f1f5f9;
        }

        /* ========== PREFERENCE CARDS ========== */
        .pf-preference-card {
          display: flex; align-items: center; gap: 14px;
          padding: 20px 22px; background: white;
          border-radius: 14px; border: 1px solid #e2e8f0;
          transition: all 0.25s;
        }
        .pf-preference-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px -6px rgba(0,0,0,0.08);
        }
        .pf-preference-icon-wrapper {
          width: 48px; height: 48px; border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          font-size: 20px; flex-shrink: 0;
        }
        .pf-preference-icon-wrapper-blue { background: #dbeafe; color: #3b82f6; }
        .pf-preference-icon-wrapper-violet { background: #ede9fe; color: #8b5cf6; }
        .pf-preference-content { flex-grow: 1; min-width: 0; }
        .pf-preference-title {
          font-weight: 700; color: #0f172a; font-size: 0.9rem; margin-bottom: 2px;
        }
        .pf-preference-desc { color: #94a3b8; font-size: 0.78rem; }

        /* Switch */
        .pf-switch {
          position: relative; display: inline-block;
          width: 48px; height: 26px; flex-shrink: 0;
        }
        .pf-switch input { opacity: 0; width: 0; height: 0; }
        .pf-switch-slider {
          position: absolute; cursor: pointer;
          top: 0; left: 0; right: 0; bottom: 0;
          background: #cbd5e1; border-radius: 26px;
          transition: 0.3s;
        }
        .pf-switch-slider:before {
          position: absolute; content: "";
          height: 20px; width: 20px; left: 3px; bottom: 3px;
          background: white; border-radius: 50%;
          transition: 0.3s; box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        .pf-switch input:checked + .pf-switch-slider {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        }
        .pf-switch input:checked + .pf-switch-slider:before {
          transform: translateX(22px);
        }

        .pf-lang-badge {
          display: inline-flex; align-items: center;
          padding: 6px 14px; border-radius: 8px;
          background: linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%);
          color: #5b21b6; font-weight: 700; font-size: 0.8rem;
          flex-shrink: 0;
        }

        /* ========== BUTTONS ========== */
        .pf-btn-primary, .pf-btn-secondary, .pf-btn-success, .pf-btn-danger, .pf-btn-cancel {
          display: inline-flex; align-items: center; justify-content: center; gap: 8px;
          padding: 12px 20px; border-radius: 10px; font-weight: 600; font-size: 0.875rem;
          border: none; transition: all 0.2s; cursor: pointer;
        }
        .pf-btn-primary {
          background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%); color: white;
        }
        .pf-btn-primary:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 8px 16px -4px rgba(139, 92, 246, 0.4);
        }
        .pf-btn-success {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%); color: white;
        }
        .pf-btn-success:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 8px 16px -4px rgba(16, 185, 129, 0.4);
        }
        .pf-btn-danger {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); color: white;
        }
        .pf-btn-danger:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 8px 16px -4px rgba(239, 68, 68, 0.4);
        }
        .pf-btn-cancel {
          background: white; border: 1px solid #e2e8f0; color: #334155;
        }
        .pf-btn-cancel:hover:not(:disabled) { background: #f1f5f9; }
        .pf-btn-primary:disabled, .pf-btn-success:disabled, .pf-btn-danger:disabled { 
          opacity: 0.5; cursor: not-allowed; transform: none !important; 
        }
        .pf-btn-full { width: 100%; }
        .pf-btn-flex { flex: 1; }

        /* ========== ALERT ========== */
        .pf-alert {
          display: flex; align-items: center; gap: 12px;
          padding: 14px 18px; border-radius: 12px;
          font-size: 0.85rem; font-weight: 500;
        }
        .pf-alert > i { font-size: 1.3rem; flex-shrink: 0; }
        .pf-alert-success { 
          background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%); 
          color: #065f46; border-left: 4px solid #10b981; 
        }
        .pf-alert-danger { 
          background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%); 
          color: #991b1b; border-left: 4px solid #ef4444; 
        }

        /* ========== MODAL ========== */
        .pf-modal .modal-content { border: none; border-radius: 16px; overflow: hidden; }
        .pf-modal-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 20px 24px; color: white;
        }
        .pf-modal-header-amber { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); }
        .pf-modal-header-success { background: linear-gradient(135deg, #10b981 0%, #059669 100%); }
        .pf-modal-header-danger { background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); }
        .pf-modal-icon {
          width: 44px; height: 44px; border-radius: 10px;
          background: rgba(255,255,255,0.2);
          display: flex; align-items: center; justify-content: center;
          font-size: 20px;
        }
        .pf-modal-title { font-weight: 700; letter-spacing: -0.01em; margin: 0; }
        .pf-modal-subtitle { color: rgba(255,255,255,0.85); font-size: 0.8rem; }
        .pf-modal-close {
          background: rgba(255,255,255,0.1); border: none; color: white;
          width: 36px; height: 36px; border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all 0.2s; font-size: 1rem;
        }
        .pf-modal-close:hover { background: rgba(255,255,255,0.2); transform: rotate(90deg); }
        .pf-modal-body {
          padding: 24px; background: #f8fafc;
          max-height: 70vh; overflow-y: auto;
        }
        .pf-modal-body-center { text-align: center; }
        .pf-modal-footer {
          padding: 16px 24px; background: white;
          border-top: 1px solid #e2e8f0;
          display: flex; justify-content: flex-end; gap: 10px;
        }

        /* ========== CONFIRM MODAL ========== */
        .pf-confirm-icon {
          width: 88px; height: 88px; border-radius: 50%;
          display: inline-flex; align-items: center; justify-content: center;
          font-size: 44px; margin-bottom: 20px;
        }
        .pf-confirm-icon-success {
          background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
          color: #059669;
          box-shadow: 0 12px 30px -10px rgba(16, 185, 129, 0.4);
        }
        .pf-confirm-icon-danger {
          background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
          color: #dc2626;
          box-shadow: 0 12px 30px -10px rgba(220, 38, 38, 0.4);
        }
        .pf-confirm-title {
          font-weight: 700; color: #0f172a;
          font-size: 1.25rem; margin-bottom: 12px;
        }
        .pf-confirm-title-danger { color: #dc2626; }
        .pf-confirm-text {
          color: #64748b; font-size: 0.875rem;
          line-height: 1.5; margin-bottom: 20px;
          max-width: 480px; margin-left: auto; margin-right: auto;
        }
        .pf-confirm-warning {
          display: flex; align-items: flex-start; gap: 12px;
          padding: 14px 18px; background: #fef2f2;
          border-radius: 12px; border-left: 4px solid #ef4444;
          color: #991b1b; text-align: left;
          max-width: 480px; margin: 0 auto 20px;
        }
        .pf-confirm-warning i { color: #dc2626; font-size: 1.3rem; flex-shrink: 0; margin-top: 2px; }

        /* ========== FORM ========== */
        .pf-form-group { margin-bottom: 4px; }
        .pf-form-label {
          display: flex; align-items: center; gap: 6px;
          font-weight: 700; color: #0f172a;
          font-size: 0.82rem; margin-bottom: 8px;
        }
        .pf-form-icon { color: #8b5cf6; font-size: 0.9rem; }
        .pf-required { color: #ef4444; }
        .pf-input-wrapper { position: relative; display: flex; align-items: center; }
        .pf-input {
          width: 100%; padding: 11px 44px 11px 14px;
          border: 2px solid #e2e8f0; border-radius: 10px;
          font-size: 0.875rem; color: #0f172a; background: white;
          transition: all 0.2s; outline: none; font-family: inherit;
        }
        .pf-input:focus {
          border-color: #8b5cf6;
          box-shadow: 0 0 0 4px rgba(139, 92, 246, 0.1);
        }
        .pf-input-toggle {
          position: absolute; right: 8px;
          background: none; border: none;
          color: #94a3b8; font-size: 1rem;
          padding: 6px 10px; cursor: pointer;
          border-radius: 6px; transition: all 0.15s;
        }
        .pf-input-toggle:hover { background: #f1f5f9; color: #8b5cf6; }
        .pf-form-hint {
          margin-top: 6px; font-size: 0.72rem; color: #94a3b8;
          display: flex; align-items: center; gap: 4px;
        }
        .pf-form-hint i { color: #8b5cf6; }

        /* Security requirements */
        .pf-security-requirements {
          background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
          border-radius: 12px; padding: 18px 20px;
          border: 1px solid #bfdbfe;
        }
        .pf-security-requirements-header {
          display: flex; align-items: center; gap: 8px;
          font-weight: 700; color: #1e40af;
          font-size: 0.85rem; margin-bottom: 12px;
        }
        .pf-security-requirements-header i { color: #3b82f6; font-size: 1.05rem; }
        .pf-security-requirements-grid {
          display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px;
        }
        .pf-security-requirement {
          display: flex; align-items: center; gap: 8px;
          font-size: 0.78rem; color: #1e40af; font-weight: 500;
        }
        .pf-security-requirement i { color: #10b981; font-size: 0.85rem; }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 992px) {
          .pf-hero-body {
            flex-direction: column;
            align-items: center;
            text-align: center;
            margin-top: -65px;
          }
          .pf-hero-info { padding-top: 0; }
          .pf-hero-badges { justify-content: center; }
        }
        @media (max-width: 768px) {
          .pf-header { padding: 20px; }
          .pf-header-title { font-size: 1.2rem; }
          .pf-hero-avatar { width: 100px; height: 100px; }
          .pf-hero-avatar-status { width: 26px; height: 26px; font-size: 13px; }
          .pf-hero-name { font-size: 1.2rem; }
          .pf-modal-body { padding: 16px; }
          .pf-security-requirements-grid { grid-template-columns: 1fr; }
          .pf-modal-footer { flex-direction: column; }
          .pf-modal-footer button { width: 100%; }
        }
      `}</style>
    </div>
  );
}